import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useSEO, useSchema } from '../../utils/seo';

export default function About() {
  useSEO({
    title: 'Über EarnProof - 15 Jahre Erfahrung mit Earning-Apps | Christopher Humpert',
    description: 'Die Geschichte hinter EarnProof. Von gescheiterten Investments zu erfolgreichen Strategien. 15 Jahre Erfahrung, 47+ getestete Apps, 14.4K€ verdient.',
    keywords: 'über earnproof, christopher humpert, earning apps erfahrung, app tester, online geld verdienen erfahrung',
    canonical: 'https://example.com/about',
  });

  useSchema({
    type: 'AboutPage',
    data: {
      name: 'Über EarnProof',
      description: 'Die Geschichte hinter dem Projekt und 15 Jahre Erfahrung mit Earning-Apps',
      url: 'https://example.com/about',
      mainEntity: {
        '@type': 'Person',
        name: 'Christopher Humpert',
        jobTitle: 'App Tester & Reviewer',
        description: '15 Jahre Erfahrung mit Online-Verdienstmöglichkeiten',
        email: 'hello@earnproof.org',
      },
    },
  });

  const timeline = [
    {
      year: '2011 – 2017',
      title: 'YouTube & erste Online-Projekte',
      description: 'In dieser Zeit habe ich begonnen, eigene Inhalte zu produzieren und verschiedene YouTube-Kanäle aufgebaut. Ich wollte verstehen, wie Reichweite, Monetarisierung und Online-Einnahmen wirklich funktionieren. Es war meine erste Erfahrung damit, wie sich digitale Projekte zu etwas Echtem entwickeln können.',
      icon: 'ri-youtube-line'
    },
    {
      year: '2013',
      title: 'Erste Versuche mit Survey-Seiten',
      description: 'Ich habe angefangen, auf klassischen Umfrage-Seiten aktiv zu sein – mit dem Ziel, die ersten Euros im Internet zu verdienen. Damals noch ohne großes Wissen, aber mit einer Menge Motivation – und der Erkenntnis, dass viele Angebote mehr versprechen, als sie halten.',
      icon: 'ri-questionnaire-line'
    },
    {
      year: '2014 – 2018',
      title: 'Krypto-Projekte & Faucets',
      description: 'Mit dem Aufkommen von Bitcoin und anderen Kryptowährungen habe ich die ersten Schritte im Mining-Bereich gemacht. Ich war bei verschiedenen Projekten und Faucets aktiv, habe investiert, experimentiert – und auch Lehrgeld gezahlt. Diese Phase hat mir gezeigt, wie wichtig Transparenz und nachhaltige Strukturen im Online-Bereich sind.',
      icon: 'ri-bit-coin-line'
    },
    {
      year: '2021 – 2024',
      title: 'Neuer Fokus: Mobile Apps',
      description: 'Mit dem Smartphone-Boom kam eine neue Generation an Möglichkeiten. Ich habe erneut begonnen, Apps und Plattformen zu testen – diesmal gezielter, mit Erfahrung im Hintergrund. Von Survey-Apps über Gaming-Rewards bis hin zu Mining-Alternativen habe ich alles ausprobiert, dokumentiert und ausgewertet.',
      icon: 'ri-smartphone-line'
    },
    {
      year: '2025',
      title: 'EarnProof Launch',
      description: 'Nach Jahren voller Tests, Fehler und echter Ergebnisse wurde EarnProof.org gegründet – als Plattform, die zeigt, was wirklich funktioniert. Hier fließt alles zusammen: meine Erfahrungen, meine Analysen und mein Ziel, anderen die Abkürzung zu geben, die ich damals nicht hatte.',
      icon: 'ri-rocket-line'
    }
  ];

  const stats = [
    { number: '47+', label: 'Apps getestet', icon: 'ri-smartphone-line' },
    { number: '3.2K€', label: 'Gesamt verdient', icon: 'ri-money-euro-circle-line' },
    { number: '1.0K€', label: 'Verluste durch Scams', icon: 'ri-error-warning-line' },
    { number: '15', label: 'Jahre Erfahrung', icon: 'ri-time-line' }
  ];

  return (
    <div className="min-h-screen bg-gray-950">
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-900/20 to-cyan-900/20"></div>
        <div className="relative max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
            Über EarnProof
            <span className="block text-2xl md:text-3xl text-teal-400 font-normal mt-2">
              Die Geschichte hinter dem Projekt
            </span>
          </h1>
          <p className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto">
            Von gescheiterten Investments zu erfolgreichen Strategien - wie aus Fehlern eine Platform für ehrliche App-Reviews wurde.
          </p>
        </div>
      </section>

      {/* Personal Story */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-900/50 border border-gray-700 rounded-2xl p-8 md:p-12">
            <div className="flex items-start space-x-6">
              <div className="w-20 h-20 bg-gradient-to-br from-teal-400 to-cyan-400 rounded-full flex items-center justify-center flex-shrink-0">
                <i className="ri-user-line text-2xl text-gray-900"></i>
              </div>
              <div>
                <h2 className="text-3xl font-bold text-white mb-4">Hi, ich bin Chris</h2>
                <div className="space-y-4 text-gray-300">
                  <p>
                    Ich beschäftige mich seit über 15 Jahren damit, wie man im Internet wirklich Geld verdienen kann.
                    Lange bevor es Apps wie FreeCash, Scrambly oder Mistplay gab, habe ich Plattformen getestet, auf denen man fürs Klicken, Spielen oder Investieren bezahlt wurde. Manche haben funktioniert – viele waren reine Zeitverschwendung.
                  </p>
                  <p>
                    In dieser Zeit habe ich viel Lehrgeld gezahlt. Ich bin auf Fake-Gewinnspiele reingefallen, auf Mining-Seiten, die nach zehn Tagen offline waren, und auf Apps, die mir 150 Euro versprochen haben, wenn ich „nur noch zehnmal Werbung schaue".
                    Diese Erfahrungen waren teuer, aber sie haben mir gezeigt, worauf es wirklich ankommt: Echtheit, Transparenz und Proof.
                  </p>
                  <p>
                    Beruflich kenne ich die andere Seite – ich weiß, wie seriöse Firmen aufgebaut sind und woran man erkennt, ob hinter einer Website wirklich jemand steht. Genau dieses Wissen lasse ich hier einfließen.
                  </p>
                  <p>
                    <strong className="text-teal-400">EarnProof.org ist kein „Werde-reich-über-Nacht“-Projekt.</strong> Ich verkaufe keine Träume, sondern zeige, was tatsächlich funktioniert – mit Screenshots, Auszahlungen und ehrlichem RealTalk.
                  </p>
                  <p>
                    Wenn du mich unterstützen willst, kannst du das tun, indem du dich über meine Affiliate-Links anmeldest. Du zahlst dadurch nichts extra, aber hilfst, dass diese Seite weiter wachsen kann.
                  </p>
                  <p>
                    <strong className="text-teal-400">Ich zwinge niemanden, ich überzeuge mit Beweisen.</strong> Willkommen bei EarnProof – Beweise statt Versprechen.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Die Zahlen sprechen für sich</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-teal-900/30 rounded-full">
                <i className="ri-smartphone-line text-2xl text-teal-400"></i>
              </div>
              <div className="text-3xl font-bold text-white mb-2">47+</div>
              <div className="text-gray-400">Apps getestet</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-teal-900/30 rounded-full">
                <i className="ri-money-euro-circle-line text-2xl text-teal-400"></i>
              </div>
              <div className="text-3xl font-bold text-white mb-2">14.4K€</div>
              <div className="text-gray-400">Gesamt verdient</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-teal-900/30 rounded-full">
                <i className="ri-error-warning-line text-2xl text-teal-400"></i>
              </div>
              <div className="text-3xl font-bold text-white mb-2">1.0K€</div>
              <div className="text-gray-400">Verluste durch Scams</div>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-teal-900/30 rounded-full">
                <i className="ri-time-line text-2xl text-teal-400"></i>
              </div>
              <div className="text-3xl font-bold text-white mb-2">15</div>
              <div className="text-gray-400">Jahre Erfahrung</div>
            </div>
          </div>
        </div>
      </section>

      {/* Timeline */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Meine Journey</h2>
          <div className="space-y-8">
            {timeline.map((item, index) => (
              <div key={index} className="flex items-start space-x-6">
                <div className="w-12 h-12 bg-teal-900/30 rounded-full flex items-center justify-center flex-shrink-0">
                  <i className={`${item.icon} text-xl text-teal-400`}></i>
                </div>
                <div className="flex-1">
                  <div className="flex items-center space-x-4 mb-2">
                    <span className="text-2xl font-bold text-teal-400">{item.year}</span>
                    <h3 className="text-xl font-semibold text-white">{item.title}</h3>
                  </div>
                  <p className="text-gray-300">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-900/30">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-white text-center mb-12">Meine Prinzipien</h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-teal-900/30 rounded-full">
                <i className="ri-shield-check-line text-2xl text-teal-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">100% Ehrlichkeit</h3>
              <p className="text-gray-300">
                Keine geschönten Zahlen, keine versteckten Affiliate-Links. Nur echte Erfahrungen und dokumentierte Beweise.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-teal-900/30 rounded-full">
                <i className="ri-microscope-line text-2xl text-teal-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Gründliche Tests</h3>
              <p className="text-gray-300">
                Jede App wird mindestens 30 Tage getestet. Mit Screenshots, Auszahlungsbeweisen und detaillierter Dokumentation.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center bg-teal-900/30 rounded-full">
                <i className="ri-community-line text-2xl text-teal-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-white mb-3">Community First</h3>
              <p className="text-gray-300">
                Diese Platform existiert für die Community. Feedback, Fragen und eigene Erfahrungen sind immer willkommen.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Fragen oder Feedback?</h2>
          <p className="text-xl text-gray-300 mb-8">
            Ich freue mich über jede Nachricht, jeden Tipp und jede Diskussion über Earning-Apps.
          </p>
          <div className="flex justify-center">
            <Button 
              variant="primary" 
              size="lg"
              onClick={() => window.REACT_APP_NAVIGATE('/contact')}
            >
              Kontakt aufnehmen
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
